import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import "../components/empieza/empieza.css";

const QV_LOGO = "https://customer-assets.emergentagent.com/job_athletic-edge-19/artifacts/7jyn9i1w_IMG_2453.PNG";

const Input = ({ label, testid, ...p }) => (
  <div className="emp-field"><label>{label}</label><input className="emp-input" data-testid={testid} {...p} /></div>
);

export default function OnboardingLink() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, ready, login, setUser, logout } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("register");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/onboarding/link/${token}`).then(({ data }) => {
      setInfo(data); setEmail(data.email || ""); setMode(data.account_exists ? "login" : "register");
    }).catch((e) => setErr(formatErr(e)));
  }, [token]);

  const claimAndGo = async () => {
    await api.post(`/onboarding/link/${token}/claim`);
    navigate("/app/incorporacion", { replace: true });
  };

  const continueAsUser = async () => {
    setBusy(true);
    try { await claimAndGo(); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && pw !== pw2) return toast.error("Las contraseñas no coinciden");
    setBusy(true);
    try {
      if (mode === "register") {
        const { data } = await api.post(`/onboarding/link/${token}/register`, { email: info.email ? undefined : email, password: pw });
        localStorage.setItem("qv_token", data.token);
        setUser(data.user);
        navigate("/app/incorporacion", { replace: true });
      } else {
        await login(email.trim().toLowerCase(), pw);
        await claimAndGo();
      }
    } catch (e2) {
      toast.error(formatErr(e2));
    } finally {
      setBusy(false);
    }
  };

  const body = () => {
    if (err) return (<div data-testid="onboarding-link-error"><h1 className="emp-q">Enlace no disponible</h1><p className="emp-help">{err}</p></div>);
    if (!info || !ready) return <div style={{ color: "#a3a3a3" }}>Cargando…</div>;
    return (
      <>
        <div className="qv-overline">Incorporación · QV Performance</div>
        <h1 className="emp-q">Hola, {info.player_name.split(" ")[0]}</h1>
        <p className="emp-help" style={{ whiteSpace: "pre-wrap", color: "#d4d4d4", fontSize: 15 }} data-testid="onboarding-link-welcome">{info.welcome_message}</p>
        {user ? (
          user.role === "admin" ? (
            <div className="emp-note" data-testid="onboarding-link-admin">Has iniciado sesión como administrador. Abre este enlace en otra ventana o en modo incógnito para verlo como el futbolista.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <p className="emp-help">Has iniciado sesión como <b>{user.email}</b>.</p>
              <button className="emp-btn" onClick={continueAsUser} disabled={busy} data-testid="onboarding-link-continue">Continuar <ArrowRight size={18} /></button>
              <button className="emp-linkbtn" onClick={logout} data-testid="onboarding-link-switch">No soy yo, cerrar sesión</button>
            </div>
          )
        ) : (
          <form onSubmit={submit} data-testid={`onboarding-${mode}-form`} style={{ marginTop: 8 }}>
            <div className="qv-overline" style={{ marginBottom: 12 }}>{mode === "register" ? "Crea tu cuenta" : "Inicia sesión"}</div>
            <Input label="Email" type="email" required value={email} readOnly={!!info.email} onChange={(e) => setEmail(e.target.value)}
              style={info.email ? { opacity: 0.7 } : undefined} testid="onboarding-email" autoComplete="email" />
            <Input label="Contraseña" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)}
              testid="onboarding-password" autoComplete={mode === "register" ? "new-password" : "current-password"} placeholder="Mínimo 6 caracteres" />
            {mode === "register" && <Input label="Repite la contraseña" type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} testid="onboarding-password2" autoComplete="new-password" />}
            <button className="emp-btn" style={{ width: "100%", marginTop: 6 }} disabled={busy} data-testid="onboarding-auth-submit">
              {busy ? "Un momento…" : mode === "register" ? "Crear cuenta y empezar" : "Entrar y continuar"} <ArrowRight size={18} />
            </button>
            <button type="button" className="emp-linkbtn" onClick={() => setMode(mode === "register" ? "login" : "register")} data-testid="onboarding-toggle-mode">
              {mode === "register" ? "Ya tengo cuenta, iniciar sesión" : "No tengo cuenta, crearla"}
            </button>
          </form>
        )}
      </>
    );
  };

  return (
    <div className="emp-root" data-testid="onboarding-link-page">
      <div className="emp-bg" aria-hidden="true"><img src={QV_LOGO} alt="" className="emp-logo-big" /><img src={QV_LOGO} alt="" className="emp-logo-small" /></div>
      <div className="emp-wrap">
        <div className="emp-head"><div className="emp-brand"><img src={QV_LOGO} alt="QV" /><div className="qv-display" style={{ fontSize: 18 }}>QV PERFORMANCE</div></div></div>
        <div className="emp-body">{body()}</div>
      </div>
    </div>
  );
}
